/* Alcaplus theme — progressive enhancements. No frameworks. */
(() => {
  document.documentElement.classList.remove('no-js');
  const lang = document.documentElement.lang || 'en';

  /* ---------------------------------------------------------------- Reveal */
  const revealTargets = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window && revealTargets.length) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    revealTargets.forEach((el) => io.observe(el));
  } else {
    revealTargets.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------------------------------------------------------------- Header */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    const setHeight = () =>
      document.documentElement.style.setProperty('--header-height', Math.round(header.getBoundingClientRect().height) + 'px');
    onScroll();
    setHeight();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setHeight, { passive: true });
  }

  document.querySelectorAll('details[data-nav-drawer]').forEach((drawer) => {
    const place = () => {
      if (!header) return;
      const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      drawer.style.setProperty('--drawer-top', bottom + 'px');
    };
    drawer.addEventListener('toggle', place);
    window.addEventListener('resize', place, { passive: true });
    drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => drawer.removeAttribute('open')));
  });

  document.querySelectorAll('[data-localization-form] select').forEach((sel) => {
    sel.addEventListener('change', () => sel.form && sel.form.submit());
  });

  /* ------------------------------------------------------------ Count-up */
  const counters = document.querySelectorAll('[data-count]');
  if (counters.length && 'IntersectionObserver' in window) {
    const run = (el) => {
      const raw = el.dataset.count.replace(',', '.');
      const target = parseFloat(raw);
      const decimals = (raw.split('.')[1] || '').length;
      const fmt = new Intl.NumberFormat(lang, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      const suffix = el.dataset.suffix || '';
      const dur = 1400;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt.format(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const cio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          run(e.target);
          cio.unobserve(e.target);
        }
      });
    });
    counters.forEach((c) => cio.observe(c));
  }

  /* ------------------------------------------------ Daily promo countdown
     Counts down to the visitor's local end-of-day (or the configured hour)
     and rolls over automatically every 24 h. */
  const countdowns = document.querySelectorAll('[data-countdown]');
  if (countdowns.length) {
    const pad = (n) => String(n).padStart(2, '0');
    const nextDeadline = (endHour) => {
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, 0, 0, 0);
      if (d <= now) d.setDate(d.getDate() + 1);
      return d;
    };
    const tick = () => {
      const now = Date.now();
      countdowns.forEach((el) => {
        const endHour = Math.min(24, Math.max(1, Number(el.dataset.endHour) || 24));
        const diff = Math.max(0, nextDeadline(endHour) - now);
        const total = Math.floor(diff / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const eh = el.querySelector('[data-cd-h]');
        const em = el.querySelector('[data-cd-m]');
        const es = el.querySelector('[data-cd-s]');
        if (eh) eh.textContent = pad(h);
        if (em) em.textContent = pad(m);
        if (es) es.textContent = pad(s);
        el.classList.add('is-tick');
        el.setAttribute('aria-label', `${pad(h)}:${pad(m)}:${pad(s)}`);
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* Click-to-copy discount code */
  document.querySelectorAll('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const code = btn.dataset.copy;
      try {
        await navigator.clipboard.writeText(code);
        const original = btn.textContent;
        btn.textContent = btn.dataset.copied || 'Copied';
        btn.classList.add('is-copied');
        setTimeout(() => {
          btn.textContent = original;
          btn.classList.remove('is-copied');
        }, 1600);
      } catch (e) {
        /* clipboard unavailable: leave the code visible */
      }
    });
  });

  /* --------------------------------------------------------------- Toast */
  const toast = document.querySelector('[data-toast]');
  let toastTimer = 0;
  const showToast = (msg) => {
    if (!toast) return;
    toast.textContent = msg;
    toast.hidden = false;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => (toast.hidden = true), 300);
    }, 2600);
  };

  /* --------------------------------------------------------- Cart drawer */
  const SECTION_ID = 'cart-drawer';
  let drawer = document.querySelector('[data-cart-drawer]');

  const updateCounts = (count) => {
    document.querySelectorAll('[data-cart-count-badge]').forEach((b) => {
      b.textContent = count;
      b.hidden = count === 0;
    });
  };

  const replaceDrawer = (html) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    const fresh = tpl.content.querySelector('[data-cart-drawer]');
    if (!fresh || !drawer) return;
    const wasOpen = drawer.open;
    drawer.replaceWith(fresh);
    drawer = fresh;
    bindDrawer();
    if (wasOpen) drawer.showModal();
    updateCounts(Number(drawer.dataset.cartCount) || 0);
  };

  const openDrawer = () => {
    if (!drawer) return;
    if (!drawer.open) drawer.showModal();
  };
  const closeDrawer = () => drawer && drawer.open && drawer.close();

  const refreshDrawer = async () => {
    const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } });
    const json = await res.json();
    if (json[SECTION_ID]) replaceDrawer(json[SECTION_ID]);
  };

  const changeLine = async (line, quantity) => {
    if (!drawer) return;
    drawer.classList.add('is-loading');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: SECTION_ID }),
      });
      const json = await res.json();
      if (json.sections && json.sections[SECTION_ID]) replaceDrawer(json.sections[SECTION_ID]);
      else await refreshDrawer();
    } finally {
      drawer && drawer.classList.remove('is-loading');
    }
  };

  function bindDrawer() {
    if (!drawer) return;
    drawer.querySelectorAll('[data-cart-close]').forEach((b) => b.addEventListener('click', closeDrawer));
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeDrawer();
    });
    drawer.querySelectorAll('[data-cart-change]').forEach((b) => {
      b.addEventListener('click', () => changeLine(Number(b.dataset.line), Number(b.dataset.quantity)));
    });
  }
  bindDrawer();

  /* Header cart icon opens the drawer */
  document.querySelectorAll('[data-cart-open]').forEach((a) => {
    a.addEventListener('click', (e) => {
      if (!drawer || typeof drawer.showModal !== 'function') return;
      e.preventDefault();
      openDrawer();
    });
  });

  /* Intercept every add-to-cart form */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    if (!drawer || typeof drawer.showModal !== 'function') return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    const original = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-busy');
    }
    try {
      const fd = new FormData(form);
      fd.append('sections', SECTION_ID);
      const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      const json = await res.json();
      if (!res.ok) {
        showToast(json.description || json.message || 'Error');
        return;
      }
      if (json.sections && json.sections[SECTION_ID]) replaceDrawer(json.sections[SECTION_ID]);
      else await refreshDrawer();
      openDrawer();
    } catch (err) {
      form.submit();
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-busy');
        btn.innerHTML = original;
      }
    }
  });

  /* ------------------------------------------------------ Offer builder
     Plan radios → hidden variant id, button price/label, value-stack price. */
  document.querySelectorAll('[data-offer]').forEach((offer) => {
    const variantInput = offer.querySelector('[data-offer-variant]');
    const button = offer.querySelector('[data-offer-button]');
    const label = offer.querySelector('[data-offer-label]');
    const price = offer.querySelector('[data-offer-price]');
    const stackPrice = offer.querySelector('[data-offer-product-price]');
    const radios = [...offer.querySelectorAll('input[type="radio"][name="plan"]')];
    if (!variantInput || !radios.length) return;

    const sync = (radio) => {
      if (!radio) return;
      variantInput.value = radio.value;
      const available = radio.dataset.available === 'true';
      if (price) price.textContent = radio.dataset.price || '';
      if (stackPrice) stackPrice.textContent = radio.dataset.price || '';
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? label.dataset.add : label.dataset.soldOut;
      radios.forEach((r) => r.closest('.offer__plan')?.classList.toggle('is-selected', r === radio));
    };

    radios.forEach((r) => r.addEventListener('change', () => sync(r)));
    sync(radios.find((r) => r.checked) || radios[0]);
  });

  /* ------------------------------------------------------------ Product */
  const pdp = document.querySelector('[data-pdp]');
  if (pdp) {
    const track = pdp.querySelector('[data-gallery-track]');
    const thumbs = [...pdp.querySelectorAll('[data-thumb]')];
    if (track && thumbs.length) {
      const setActive = (i) => {
        thumbs.forEach((t, idx) => {
          const on = idx === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      };
      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const i = Number(t.dataset.thumb);
          track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
          setActive(i);
        });
      });
      let raf = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / track.clientWidth)));
        },
        { passive: true }
      );
    }

    pdp.querySelectorAll('[data-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.qty));
      });
    });

    const select = pdp.querySelector('[data-variant-select]');
    if (select) {
      const cur = pdp.querySelector('[data-price-current]');
      const cmp = pdp.querySelector('[data-price-compare]');
      const add = pdp.querySelector('[data-add-button]');
      const addLabel = pdp.querySelector('[data-add-label]');
      const stickyPrice = pdp.querySelector('[data-sticky-price]');
      const soldOut = addLabel ? addLabel.dataset.soldOut : '';
      const addText = addLabel ? addLabel.textContent.trim() : '';
      select.addEventListener('change', () => {
        const opt = select.selectedOptions[0];
        if (!opt) return;
        if (cur) cur.textContent = opt.dataset.price;
        if (cmp) {
          cmp.textContent = opt.dataset.compare || '';
          cmp.hidden = !opt.dataset.compare;
        }
        if (stickyPrice) stickyPrice.textContent = opt.dataset.price;
        const available = opt.dataset.available === 'true';
        if (add) add.disabled = !available;
        if (addLabel) addLabel.textContent = available ? addText : soldOut;
        const url = new URL(location.href);
        url.searchParams.set('variant', opt.value);
        history.replaceState(null, '', url);
      });
    }

    const bar = pdp.querySelector('[data-sticky-bar]');
    const addBtn = pdp.querySelector('[data-add-button]');
    if (bar && addBtn && 'IntersectionObserver' in window) {
      bar.hidden = false;
      const sio = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      );
      sio.observe(addBtn);
    }
  }
})();
